// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    labour: state.pdfSettings.parameters.labourCharges,
    freight: state.pdfSettings.parameters.freightCharges,
    shopSupplies: state.pdfSettings.parameters.shopSupplies,
    quote: state.xMyCustomQuoteDetails.quote,
});

type Props = ReturnType<typeof mapStateToProps>;

export interface CustomQuoteTotalStyles {
    container?: InjectableCss;
    innerWrapper?: InjectableCss;
    row?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    quoteTotalWrapper?: InjectableCss;
    quoteTotalValue?: TypographyPresentationProps;
}
export const styles: CustomQuoteTotalStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    innerWrapper: {
        css: css`
            @media (max-width: 600px) {
                width: 100%;
            }
        `,
    },
    row: {
        css: css`
            min-width: 230px;
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            @media (max-width: 600px) {
                width: 100%;
            }
        `,
    },
    label: {
        size: "12px",
        lineHeight: "14px",
        css: css`
            white-space: nowrap;
        `,
    },
    value: {
        size: "13px",
        lineHeight: "15px",
        weight: "bold",
        css: css`
            display: block;
            flex-grow: 1;
            text-align: right;
            padding-left: 5px;
        `,
    },
    quoteTotalWrapper: {
        css: css`
            min-width: 230px;
            padding-top: 20px;
            margin-top: 20px;
            border-top: 1px solid ${getColor("common.ui2")};
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    quoteTotalValue: {
        size: "18px",
        lineHeight: "21px",
        weight: "bold",
        css: css`
            text-align: right;
            padding-left: 5px;
        `,
    },
};

const QuoteTotal = ({ quote, labour, freight, shopSupplies }: Props) => {
    const [quoteTotal, setQuoteTotal] = useState(0);
    const [productsTotal, setProductsTotal] = useState(0);

    if (!quote) {
        return null;
    }

    useEffect(() => {
        const productsTot = quote.linesModel.reduce((total, product) => {
            return total + Number(product.extendedPrice) * Number(product.qty);
        }, 0);
        setProductsTotal(productsTot);
    }, [quote]);

    useEffect(() => {
        const total = Number(labour) + Number(freight) + Number(shopSupplies) + productsTotal;
        setQuoteTotal(total);
    }, [productsTotal, labour, freight, shopSupplies]);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Products total:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={productsTotal} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Labour:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={labour} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Freight:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={freight} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Shop Supplies:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={shopSupplies} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.quoteTotalWrapper}>
                    <Typography {...styles.label}>{translate("Quote Total:")}</Typography>
                    <Typography {...styles.quoteTotalValue}>
                        <LocalizedCurrency amount={quoteTotal} />
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(QuoteTotal);
